import { ComponentProps } from 'lib/component-props';
import PressReleaseCard from 'components/Tiles/PressReleaseCard';
import LoadMoreButton from 'components/includes/LoadMoreButton';
import { useEffect, useMemo, useState } from 'react';
import { useLazyArticlesSearchQuery } from '@/store/articles/services/articleSearch';
import { useSelector } from 'react-redux';
import { getArticlesData } from '@/store/articles';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { ArticleSearchResponse, LazyQuery } from 'model/Article';

const PressReleaseCards = ({ rendering }: ComponentProps) => {
  const [fetchPressReleases, { data, isFetching, error }] =
    useLazyArticlesSearchQuery<LazyQuery<ArticleSearchResponse>>();
  const [pressReleases, setPressReleases] = useState<Array<ArticleSearchResponse>>([]);
  const [currentPage, setPage] = useState(0);
  const { sitecoreContext } = useSitecoreContext();
  const { appliedFilters, contentType } = useSelector(getArticlesData);

  const deviceType = useWindowDimensions();
  const postsPerPage = deviceType === 'mobile' ? 6 : 12;

  const totalPages = useMemo(() => {
    return (data && Math.ceil(data?.total_item / postsPerPage) - 1) ?? null;
  }, [data, postsPerPage]);

  const handleFetchPressReleases = async (page = 0) => {
    setPage(page);
    await fetchPressReleases({
      filters: appliedFilters,
      search: {
        sites: [sitecoreContext.site?.name],
        language: sitecoreContext.language,
        entityTypes: ['pressRelease'],
        contentTypes: (contentType && [contentType]) || null,
      },
      take: postsPerPage,
      skip: postsPerPage * page,
    });
  };

  useEffect(() => {
    if (deviceType !== 'loading') {
      setPressReleases([]);
      handleFetchPressReleases();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, sitecoreContext.language, sitecoreContext.site?.name, deviceType]);

  useEffect(() => {
    if (data?.content) {
      setPressReleases((prevPressReleases) => [...prevPressReleases, ...data.content]);
    }
  }, [data]);

  if (!data || error) return null;

  return (
    <div data-e2e-component-uid={rendering.uid} className="container py-4 background-white">
      {pressReleases.length !== 0 && (
        <div className="row d-flex flex-wrap">
          {pressReleases.map((article) => (
            <div className="col-12 col-md-6 col-lg-4 mb-3 mb-md-4" key={article.id}>
              <div className="h-100">
                <PressReleaseCard fields={article} />
              </div>
            </div>
          ))}
        </div>
      )}
      <LoadMoreButton
        isFetching={isFetching}
        currentPage={currentPage}
        maxPages={totalPages}
        className="d-flex mx-auto mt-1 mt-md-0"
        data-e2e-component-uid={rendering.uid + 'load-more'}
        loadMore={handleFetchPressReleases}
      />
    </div>
  );
};

export default PressReleaseCards;
